import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { loginSuccess } from '../store/slices/authSlice';
import { validateToken } from '../services/authService';
import { useTokenExpiration } from '../hooks/useTokenExpiration';

// Layouts
import AuthLayout from '../components/layouts/AuthLayout';
import MainLayout from '../components/layouts/MainLayout';

// Componentes para proteger rutas
import PrivateRoute from '../components/ui/PrivateRoute';
import PermissionRoute from '../components/ui/PermissionRoute';

// Importar utilidades de roles
import { isAdminRole } from '../consts/roles';

// Carga perezosa de pu00e1ginas
const Login = lazy(() => import('../pages/auth/Login'));
const Register = lazy(() => import('../pages/auth/Register'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const ChangePassword = lazy(() => import('../pages/auth/ChangePassword'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const UserList = lazy(() => import('../pages/users/UserList'));
const RoleList = lazy(() => import('../pages/users/RoleList'));
const PermissionList = lazy(() => import('../pages/users/PermissionList'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Importar los componentes de órdenes
const OrderList = React.lazy(() => import('../pages/orders/OrderListPaginated'));
const OrderDetail = React.lazy(() => import('../pages/orders/OrderDetail'));
const OrderForm = React.lazy(() => import('../pages/orders/OrderForm'));

// Importar los componentes de clientes y equipos
const CustomerList = React.lazy(() => import('../pages/customers/CustomerList'));
const EquipmentList = React.lazy(() => import('../pages/equipment/EquipmentList'));

// Importar el nuevo componente de personas
const PersonList = React.lazy(() => import('../pages/persons/PersonList'));

// Importar los componentes de inventario
const InventoryList = React.lazy(() => import('../pages/inventory/InventoryList'));
const InventoryDetail = React.lazy(() => import('../pages/inventory/InventoryDetail'));
const InventoryMigrationPage = React.lazy(() => import('../pages/inventory/InventoryMigrationPage'));
const InventoryHome = React.lazy(() => import('../pages/inventory/InventoryHome'));

// Importar componentes de gestión de entidades relacionadas
const SupplierList = React.lazy(() => import('../pages/inventory/suppliers/SupplierList'));
const CategoryList = React.lazy(() => import('../pages/inventory/categories/CategoryList'));
const LocationList = React.lazy(() => import('../pages/inventory/locations/LocationList'));
const WorkerList = React.lazy(() => import('../pages/inventory/workers/WorkerList'));
const RequestList = React.lazy(() => import('../pages/inventory/requests/RequestList'));
const RequestApprovalList = React.lazy(() => import('../pages/inventory/requests/approval/RequestApprovalList'));
const RequestApprovalDetail = React.lazy(() => import('../pages/inventory/requests/approval/RequestApprovalDetail'));
const AssignItemEquipment = React.lazy(() => import('../pages/inventory/requests/AssignItemEquipment'));

// Importar el componente de producción
const ProductionHome = React.lazy(() => import('../pages/production/ProductionHome'));
const ProductionRequestList = React.lazy(() => import('../pages/production/requests/RequestList'));
const ProductionRequestDetail = React.lazy(() => import('../pages/production/requests/RequestDetail'));
const ProductionRequestFormPage = React.lazy(() => import('../pages/production/requests/RequestFormPage'));
const ProductionItems = React.lazy(() => import('../pages/production/items/ProductionItems'));
const ProductionReturnFormPage = React.lazy(() => import('../pages/production/returns/ReturnFormPage'));

// Importar componentes de devoluciones
const ReturnApprovalList = React.lazy(() => import('../pages/inventory/returns/approval/ReturnApprovalList'));
const ReturnApprovalDetail = React.lazy(() => import('../pages/inventory/returns/approval/ReturnApprovalDetail'));

// Importar el componente de migración de archivos
const FileMigrationPage = React.lazy(() => import('../pages/file-migration/FileMigrationPage'));

// Importar el componente de inicio genérico
const Home = React.lazy(() => import('../pages/Home'));

// Importar componentes de Ingeniería (BOM)
const EngineeringHome = React.lazy(() => import('../pages/engineering/EngineeringHome'));
const BomList = React.lazy(() => import('../pages/engineering/bom/BomList'));
const BomForm = React.lazy(() => import('../pages/engineering/bom/BomForm'));
const BomCsvImportPage = React.lazy(() => import('../pages/engineering/bom/BomCsvImportPage'));

// Componente para mostrar durante la carga
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader"></div>
  </div>
);

// Componente para redirigir según el rol del usuario
const HomeRedirect: React.FC = () => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Debug: verificar datos del usuario
  console.log('HomeRedirect - User:', user);
  console.log('HomeRedirect - Role:', user?.role);
  console.log('HomeRedirect - DefaultDashboard:', user?.role?.defaultDashboard);

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const defaultDashboard = user?.role?.defaultDashboard;

  // Si el rol tiene un dashboard por defecto configurado, redirigir allí
  if (defaultDashboard) {
    console.log('HomeRedirect - Redirigiendo a:', defaultDashboard);
    return <Navigate to={defaultDashboard} replace />;
  }

  // Fallback: si es admin, redirigir al dashboard
  const roleName = user?.role?.name;
  console.log('HomeRedirect - Role name:', roleName);
  if (isAdminRole(roleName)) {
    console.log('HomeRedirect - Redirigiendo a /dashboard (admin)');
    return <Navigate to="/dashboard" replace />;
  }

  // Todos los demás roles van a la vista genérica de inicio
  console.log('HomeRedirect - Redirigiendo a /home (fallback)');
  return <Navigate to="/home" replace />;
};

// Componente wrapper para Dashboard que redirige según el rol
const DashboardRoute: React.FC = () => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const roleName = user?.role?.name;
  const defaultDashboard = user?.role?.defaultDashboard;

  // Si el rol tiene un dashboard por defecto configurado y no es el dashboard general, redirigir allí
  if (defaultDashboard && defaultDashboard !== '/dashboard') {
    console.log('DashboardRoute - Redirigiendo a defaultDashboard:', defaultDashboard);
    return <Navigate to={defaultDashboard} replace />;
  }

  // Si no es admin, redirigir a la vista genérica de inicio
  if (!isAdminRole(roleName)) {
    return <Navigate to="/home" replace />;
  }

  // Solo admins pueden ver el dashboard
  return <Dashboard />;
};

export const AppRoutes: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  useTokenExpiration();

  useEffect(() => {
    const verifyToken = async () => {
      if (!isAuthenticated) {
        try {
          const userData = await validateToken();
          if (userData) {
            dispatch(loginSuccess({
              user: userData,
              token: localStorage.getItem('auth_token') || '',
              permissions: userData.permissions || [],
              isSuperAdmin: userData.isSuperAdmin || false,
            }));
          }
        } catch (error) {
          console.error('Error al validar token:', error);
        }
      }
    };

    verifyToken();
  }, [dispatch, isAuthenticated]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Rutas pu00fablicas */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Rutas privadas con layout principal */}
        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/home" element={<Home />} />
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/change-password" element={<ChangePassword />} />
            
            {/* Rutas protegidas por permisos */}
            <Route element={<PermissionRoute permission="users:read" />}>
              <Route path="/dashboard/users" element={<UserList />} />
            </Route>
            
            <Route element={<PermissionRoute permission="roles:read" />}>
              <Route path="/dashboard/users/roles" element={<RoleList />} />
            </Route>
            
            <Route element={<PermissionRoute permission="permissions:read" />}>
              <Route path="/dashboard/users/permissions" element={<PermissionList />} />
            </Route>

            <Route path="/orders" element={<OrderList />} />
            <Route path="/orders/new" element={<OrderForm />} />
            <Route path="/orders/:orderId" element={<OrderDetail />} />
            
            {/* Rutas para clientes, personas y equipos */}
            <Route path="/customers" element={<CustomerList />} />
            <Route path="/persons" element={<PersonList />} />
            <Route path="/equipment" element={<EquipmentList />} />
            
            {/* Rutas para Inventario */}
            <Route element={<PermissionRoute permission="inventory:read" />}>
              <Route path="/inventory/home" element={<InventoryHome />} />
              <Route path="/inventory" element={<InventoryList />} />
              <Route path="/inventory/:id" element={<InventoryDetail />} />
            </Route>
            
            <Route element={<PermissionRoute permission="inventory:manage" />}>
              <Route path="/inventory/migration" element={<InventoryMigrationPage />} />
              <Route path="/inventory/suppliers" element={<SupplierList />} />
              <Route path="/inventory/categories" element={<CategoryList />} />
              <Route path="/inventory/locations" element={<LocationList />} />
              <Route path="/inventory/workers" element={<WorkerList />} />
            </Route>
            
            {/* Rutas para Solicitudes */}
            <Route element={<PermissionRoute permission="production:read-request" />}>
              <Route path="/inventory/requests" element={<RequestList />} />
            </Route>
            
            {/* Rutas para Aprobación de Solicitudes (Inventario) */}
            <Route element={<PermissionRoute permission="inventory:read-request" />}>
              <Route path="/inventory/requests/approval" element={<RequestApprovalList />} />
              <Route path="/inventory/requests/approval/:id" element={<RequestApprovalDetail />} />
            </Route>
            
            {/* Rutas para Asignación de Items a Equipos (Inventario) */}
            <Route element={<PermissionRoute permission="inventory:assign-item-equipment" />}>
              <Route path="/inventory/requests/assign" element={<AssignItemEquipment />} />
            </Route>
            
            {/* Rutas para Producción */}
            <Route element={<PermissionRoute anyPermissions={['production:read-request', 'production:create-request', 'production:create-return']} />}>
              <Route path="/production" element={<ProductionHome />} />
              <Route path="/production/home" element={<ProductionHome />} />
            </Route>
            
            <Route element={<PermissionRoute permission="production:read-request" />}>
              <Route path="/production/items" element={<ProductionItems />} />
              <Route path="/production/requests/list" element={<ProductionRequestList />} />
              <Route path="/production/requests/:id" element={<ProductionRequestDetail />} />
            </Route>
            
            <Route element={<PermissionRoute permission="production:create-request" />}>
              <Route path="/production/requests" element={<ProductionRequestFormPage />} />
            </Route>
            
            <Route element={<PermissionRoute permission="production:create-return" />}>
              <Route path="/production/returns" element={<ProductionReturnFormPage />} />
            </Route>
            
            {/* Rutas para Aprobación de Devoluciones (Inventario) */}
            <Route element={<PermissionRoute permission="inventory:process-return" />}>
              <Route path="/inventory/returns/approval" element={<ReturnApprovalList />} />
              <Route path="/inventory/returns/approval/:id" element={<ReturnApprovalDetail />} />
            </Route>
            
            {/* Ruta para migración de archivos */}
            <Route element={<PermissionRoute anyPermissions={['file-migration:upload', 'file-migration:reset']} />}>
              <Route path="/file-migration" element={<FileMigrationPage />} />
            </Route>
            
            {/* Rutas de Ingeniería (BOM) */}
            <Route element={<PermissionRoute permission="bom:read" />}>
              <Route path="/engineering" element={<EngineeringHome />} />
              <Route path="/engineering/bom/:engineeringEquipmentId" element={<BomList />} />
            </Route>
            <Route element={<PermissionRoute permission="bom:update" />}>
              <Route path="/engineering/bom/:engineeringEquipmentId/:id/edit" element={<BomForm />} />
            </Route>
            <Route element={<PermissionRoute permission="bom:create" />}>
              <Route path="/engineering/bom/:engineeringEquipmentId/new" element={<BomForm />} />
              <Route path="/engineering/bom/:engineeringEquipmentId/import" element={<BomCsvImportPage />} />
            </Route>
          </Route>
        </Route>

        {/* Ruta de inicio - redirige según el rol */}
        <Route path="/" element={<HomeRedirect />} />
        
        {/* Ruta 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}; 